import { getUnitsByFeatures } from '../units/units';
import { applyFilters } from '../filters/filter-applicator';

/**
 * Beräknar data för valda områden och units
 * @param {Array} selectedAreas - Array av GeoJSON features
 * @param {Array} units - Array av units att filtrera och beräkna på
 * @param {Object} salesDistrictFilters - Optional: Filter object med unitsIncludeExclude
 * @param {Array} allUnitsOriginal - Optional: Original allUnits array för att hitta inkluderade units
 * @returns {Object} { salesDistrictArea, numberOfUnits, turnOver, salesArea }
 */
export function calculateDistrictData(selectedAreas, units, salesDistrictFilters = null, allUnitsOriginal = null) {
    if (!selectedAreas || selectedAreas.length === 0) {
        return {
            salesDistrictArea: 0,
            numberOfHits: 0,
            turnOver: 0,
            salesArea: 0,
            units:[]
        };
    }

    // Beräkna total area från alla valda områden
    const salesDistrictArea = selectedAreas.reduce((sum, feature) => {
        return sum + (feature.properties?.area || 0);
    }, 0);

    // Hämta units som ligger inom de valda områdena
    let filteredUnitsInArea = getUnitsByFeatures(selectedAreas, units);
    
    // Lägg till inkluderade units från filtret även om de ligger utanför valda områden
    if (salesDistrictFilters && salesDistrictFilters.unitsIncludeExclude && salesDistrictFilters.unitsIncludeExclude.length > 0) {
        const included = salesDistrictFilters.unitsIncludeExclude.filter(inc => inc.include);
        if (included.length > 0) {
            const unitsToSearch = allUnitsOriginal || units;
            included.forEach(inc => {
                const unit = unitsToSearch.find(u => u.delfiId === inc.delfiId);
                if (unit && !filteredUnitsInArea.find(u => u.delfiId === unit.delfiId)) {
                    // Lägg till unit om den inte redan finns i filteredUnitsInArea
                    filteredUnitsInArea.push(unit);
                }
            });
        }
    }
    
    // Beräkna aggregerade värden
    const numberOfHits = filteredUnitsInArea.length;
    const turnOver = filteredUnitsInArea.reduce((sum, unit) => sum + (unit.turnover || 0), 0);
    const salesArea = filteredUnitsInArea.reduce((sum, unit) => sum + (unit.retailArea || 0), 0);

    return {
        salesDistrictArea,
        numberOfHits,
        turnOver,
        salesArea,
        units: filteredUnitsInArea
    };
}

/**
 * Hämta classification filters
 */
function getClassificationFilters(classification) {
    return {
        chains: classification.chains,
        turnovers: classification.turnovers,
        salesSpaceGroups: classification.salesSpaceGroups,
        segments: classification.segments,
        unitsIncludeExclude: classification.unitsIncludeExclude
    };
}

/**
 * Hämta units för en classification
 */
function getClassificationUnits(classification, baseUnits, type, allUnits) {
    return applyFilters(baseUnits, getClassificationFilters(classification), type, allUnits);
}

/**
 * Beräkna metrics för en classification
 */
function calculateClassificationMetrics(classification, baseUnits, type, allUnits) {
    const selectedFrequency =
        classification.classificationFrequencies?.find(freq => freq.selected) ||
        classification.classificationFrequencies?.[0];
    const frequencyValue = Number(selectedFrequency?.classificationFrequencyValue) || 0;

    if (!frequencyValue) {
        return { visitingTime: 0, frequency: 0 };
    }

    const selectedVisitingTime =
        classification.classificationVisitingTimes?.find(time => time.selected) ||
        classification.classificationVisitingTimes?.[0];
    const visitingTimeMinutes = Number(selectedVisitingTime?.classificationVistingTimeValue) || 0;

    const unitsForClassification = getClassificationUnits(classification, baseUnits, type, allUnits);
    const unitCount = unitsForClassification.length;

    return {
        visitingTime: visitingTimeMinutes ? visitingTimeMinutes * unitCount * frequencyValue : 0,
        frequency: unitCount * frequencyValue
    };
}

/**
 * Beräknar frequency och vistingTimeHours baserat på classifications och units
 * @param {Array} classifications - Classifications från cache
 * @param {Array} filteredUnits - Units efter filter
 * @param {string} type - District type
 * @param {Array} allUnits - All units
 * @returns {Object} { frequency, vistingTimeHours }
 */
export function calculateClassificationData(classifications, filteredUnits, type, allUnits) {
    if (!classifications || classifications.length === 0) {
        return {
            frequency: 0,
            vistingTimeHours: 0
        };
    }
    
    const totals = classifications.reduce(
        (acc, classification) => {
            const metrics = calculateClassificationMetrics(
                classification,
                filteredUnits,
                type,
                allUnits
            );

            return {
                visitingTime: acc.visitingTime + metrics.visitingTime,
                frequency: acc.frequency + metrics.frequency
            };
        },
        { visitingTime: 0, frequency: 0 }
    );

    return {
        frequency: totals.frequency,
        vistingTimeHours: totals.visitingTime
    };
}

